import {
  Button,
  Flex,
  notify,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
  TextField,
  useLayout,
} from '@eleveo/components-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useField, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { useIntegrations } from '../../../../app/IntegrationsProvider';
import { TEST_IDS } from '../../../../constants/dataTestIds';
import { getSupportedStreams } from '../../../../scene/integrations/config';
import {
  ADHERENCE_FIELDS,
  HISTORICAL_DATA_FIELDS,
  REAL_TIME_DATA_FIELDS,
  STREAM_NAMES,
  TASK_NAMES,
  USER_SYNC_IMPORT_TEAMS_OPTIONS,
  USER_SYNCHRONIZATION_FIELDS,
} from '../../../../scene/integrations/types';
import { getStreamFormOrder } from '../../../../utils/formUtils';
import { INTEGRATIONS_WITH_ROLE_MAPPING, INTEGRATIONS_WITH_TEAM_SELECTOR, Values } from '../../../configuration/types';
import { WarningModal } from '../../../modals/WarningModal';
import { ConflictedRolesField } from '../../fields/ConflictedRolesField';
import { useIntegrationRoles } from '../../roles/IntegrationRolesProvider';

export interface UserSynchronizationProps {
  props: {
    expandedJobs: string[];
  };
}

export const UserSynchronization: React.FC<UserSynchronizationProps> = ({ props: { expandedJobs } }) => {
  const { isInReadOnlyMode, integrationType } = useIntegrations();

  const { t } = useTranslation();
  const { batch, change } = useForm();
  const { sidebar } = useLayout();

  const isSideBarExpanded = sidebar.isExpanded;

  const {
    conflictedRoles,
    conflictedOptions,
    isEleveoRolesLoading,
    isIntegrationRolesLoading,
    isPermittedForIntegrationRoles,
    isPermittedForEleveoRoles,
  } = useIntegrationRoles();

  const [isDialogActive, setIsDialogActive] = useState(false);
  const availableStreams = useMemo(() => getSupportedStreams(integrationType), [integrationType]);
  const realTimeDataOrder = getStreamFormOrder(STREAM_NAMES.realTimeData, availableStreams);

  const {
    input: { value: isAdherenceEnabled },
  } = useField(ADHERENCE_FIELDS.scheduled, { subscription: { value: true } });
  const {
    input: { value: isHistoricalEnabled },
  } = useField(HISTORICAL_DATA_FIELDS.scheduled, { subscription: { value: true } });
  const {
    input: { value: isRealTimeDataEnabled },
  } = useField(REAL_TIME_DATA_FIELDS(realTimeDataOrder).enabled, { subscription: { value: true } });
  const {
    input: { value: importAllTeams, onChange: importAllTeamsOnChange },
  } = useField(USER_SYNCHRONIZATION_FIELDS.importAllTeams, { subscription: { value: true } });

  const { input: scheduledInput } = useField(USER_SYNCHRONIZATION_FIELDS.scheduled);
  const { input: cronInput, meta: cronMeta } = useField(USER_SYNCHRONIZATION_FIELDS.cronExpression);

  useEffect(() => {
    if (
      isEleveoRolesLoading ||
      isIntegrationRolesLoading ||
      !isPermittedForIntegrationRoles ||
      !isPermittedForEleveoRoles
    ) {
      return;
    }

    const notifyConflictedOptions = conflictedOptions.length && !isSideBarExpanded;
    if (notifyConflictedOptions) {
      notify.warning(
        t('myConfigurations.roleMapping.inconsistency.options', { providerName: t(`providers.${integrationType}`) })
      );
    }

    const notifyConflictedRoles = Object.keys(conflictedRoles).length && !isSideBarExpanded;
    if (notifyConflictedRoles) {
      notify.warning(t('myConfigurations.roleMapping.inconsistency.roles'));
    }

    if (
      (importAllTeams === false && expandedJobs.includes(TASK_NAMES.userSynchronization(integrationType))) ||
      notifyConflictedOptions ||
      notifyConflictedRoles
    ) {
      sidebar.set({ expanded: true });
    }

    if (importAllTeams || !expandedJobs.includes(TASK_NAMES.userSynchronization(integrationType))) {
      sidebar.set({ expanded: false });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conflictedOptions, conflictedRoles, importAllTeams, expandedJobs]);

  const handleUserSynchronizationToggle = useCallback(
    (value: boolean) => {
      if (!value && (isAdherenceEnabled || isHistoricalEnabled || isRealTimeDataEnabled)) {
        setIsDialogActive(true);
        return;
      }

      scheduledInput.onChange(value);
    },
    [isAdherenceEnabled, isHistoricalEnabled, isRealTimeDataEnabled, scheduledInput]
  );

  const handleDialogAccept = useCallback(() => {
    batch(() => {
      change(USER_SYNCHRONIZATION_FIELDS.scheduled, false);
      change(ADHERENCE_FIELDS.scheduled, false);
      change(HISTORICAL_DATA_FIELDS.scheduled, false);
      change(REAL_TIME_DATA_FIELDS(realTimeDataOrder).enabled, false);
    });
    setIsDialogActive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectTeamImport = (value: Values<typeof USER_SYNC_IMPORT_TEAMS_OPTIONS>) => {
    const shouldImportAllTeams = value === USER_SYNC_IMPORT_TEAMS_OPTIONS.importAllTeams;
    importAllTeamsOnChange(shouldImportAllTeams);
    sidebar.set(() => ({ expanded: !shouldImportAllTeams }));
  };

  return (
    <>
      {!!Object.keys(conflictedRoles).length && <ConflictedRolesField conflictedRoles={conflictedRoles} />}
      <WarningModal
        isOpen={isDialogActive}
        title={t('myConfigurations.disableUserSynchronization.title')}
        cancelCaption={t('myConfigurations.disableUserSynchronization.cancel')}
        acceptCaption={t('myConfigurations.disableUserSynchronization.yes')}
        onAccept={handleDialogAccept}
        onCancel={() => setIsDialogActive(false)}
      >
        <Text>{t('myConfigurations.disableUserSynchronization.description')}</Text>
      </WarningModal>
      <Stack direction="vertical" className="task-configuration p-20 w-full" spacing={20}>
        <Switch
          className="w-full"
          isSelected={scheduledInput.value}
          isDisabled={isInReadOnlyMode}
          {...scheduledInput}
          onChange={handleUserSynchronizationToggle}
          testId={TEST_IDS.checkboxUserSynchronizationEnabled}
        >
          {t(`myConfigurations.tasks.${scheduledInput.value ? 'on' : 'off'}`)}
        </Switch>

        <TextField
          isRequired={scheduledInput.value || importAllTeams === false}
          isReadOnly={isInReadOnlyMode}
          label={t('myConfigurations.tasks.cronExpression')}
          className="w-full task-configuration__cron-expression"
          validationState={cronMeta.invalid ? 'invalid' : 'valid'}
          errorMessage={t(cronMeta.error)}
          {...cronInput}
          testId={TEST_IDS.inputCronExpression}
        />

        {(INTEGRATIONS_WITH_ROLE_MAPPING as readonly string[]).includes(integrationType.toLowerCase()) && (
          <Flex gap={15}>
            <Button
              isSelected={isSideBarExpanded}
              variant="outline"
              onClick={() => sidebar.set((v) => ({ expanded: !v.expanded }))}
              type="button"
              className="justify-start mis-auto"
              testId={TEST_IDS.buttonToggleRoleMapping}
            >
              {t('myConfigurations.roleMapping.title')}
            </Button>
          </Flex>
        )}

        {(INTEGRATIONS_WITH_TEAM_SELECTOR as readonly string[]).includes(integrationType.toLowerCase()) && (
          <Flex gap={15}>
            <RadioGroup
              aria-label={t('myConfigurations.teamSelector.filterOptions')}
              defaultValue={
                importAllTeams
                  ? USER_SYNC_IMPORT_TEAMS_OPTIONS.importAllTeams
                  : USER_SYNC_IMPORT_TEAMS_OPTIONS.teamsImportFilter
              }
              onChange={(value) => handleSelectTeamImport(value as Values<typeof USER_SYNC_IMPORT_TEAMS_OPTIONS>)}
            >
              <Stack direction="horizontal" spacing={10}>
                <Radio value={USER_SYNC_IMPORT_TEAMS_OPTIONS.importAllTeams}>
                  {t('myConfigurations.teamSelector.options.importAllTeams')}
                </Radio>
                <Radio value={USER_SYNC_IMPORT_TEAMS_OPTIONS.teamsImportFilter}>
                  {t('myConfigurations.teamSelector.options.teamsImportFilter')}
                </Radio>
              </Stack>
            </RadioGroup>
          </Flex>
        )}
      </Stack>
    </>
  );
};
